import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Plus, Search, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientList = () => {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.email?.toLowerCase().includes(search.toLowerCase()) ||
    client.phone?.includes(search)
  );

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="client-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {t("clients")}
            </p>
            <h1 className="font-serif text-3xl md:text-4xl">
              {t("your_clients")}
            </h1>
          </div>
          <Link to="/clients/new">
            <Button className="btn-primary" data-testid="new-client-button">
              <Plus size={16} className="mr-2" />
              Adicionar cliente
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder={t("search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12"
            data-testid="search-input"
          />
        </div>

        {/* Clients Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-32 loading-skeleton" />
            ))}
          </div>
        ) : filteredClients.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className="client-card"
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border flex items-center justify-center bg-secondary shrink-0">
                    <span className="font-serif text-lg">
                      {client.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-serif text-lg truncate">{client.name}</h3>
                    {client.phone && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Phone size={12} />
                        {client.phone}
                      </p>
                    )}
                    {client.email && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                        <Mail size={12} />
                        {client.email}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <p className="empty-state-title">
              {search ? t("no_client_found") : t("no_clients")}
            </p>
            <p className="empty-state-description">
              {search 
                ? t("try_another_search")
                : t("no_clients_message")}
            </p>
            {!search && (
              <Link to="/clients/new" className="mt-6 inline-block">
                <Button className="btn-primary">
                  <Plus size={16} className="mr-2" />
                  {t("new_client")}
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientList;
